import type { Edge } from "@xyflow/react";
import {
  DATA_MODEL_DEFAULT_HANDLES,
  KG_HANDLE_IDS,
  type KgEdgeHandleIds,
} from "../../components/kg/canvas/utils/kgEdgeHandles";
import {
  fontFamilies,
  fontSizes,
  fontWeights,
} from "../../design-system/tokens/typography";
import { rawColors } from "../../design-system/tokens/colors";
import type {
  DataModelCardinality,
  DataModelJoinType,
  DataModelManualRelation,
} from "./dataModelRelation.types";

/** Bezier edge (`type: "default"`) with curvature for auto-separated relation lines. */
export type DataModelRelationFlowEdge = Edge<
  Record<string, unknown>,
  "default"
> & {
  pathOptions?: { curvature?: number };
};

export const tableIdToNodeId = (tableId: string): string => `table-${tableId}`;

export const nodeIdToTableId = (nodeId: string): string | null =>
  nodeId.startsWith("table-") ? nodeId.slice("table-".length) : null;

const JOIN_TYPE_LABELS: Record<DataModelJoinType, string> = {
  inner: "Inner",
  left: "Left",
  right: "Right",
  full: "Full",
};

export const formatCardinalityShort = (
  cardinality: DataModelCardinality,
): string => {
  switch (cardinality) {
    case "one_to_one":
      return "1:1";
    case "one_to_many":
      return "1:N";
    case "many_to_one":
      return "N:1";
    case "many_to_many":
      return "N:N";
    default:
      return cardinality;
  }
};

export const formatJoinTypeLabel = (joinType: DataModelJoinType): string =>
  JOIN_TYPE_LABELS[joinType];

/** Edge badge: `source_col → target_col · Join · 1:N` */
export const formatRelationEdgeLabel = (
  relation: Pick<
    DataModelManualRelation,
    "sourceColumn" | "targetColumn" | "cardinality" | "joinType"
  >,
): string =>
  `${relation.sourceColumn} → ${relation.targetColumn} · ${formatJoinTypeLabel(relation.joinType)} · ${formatCardinalityShort(relation.cardinality)}`;

const edgeLabelColor = rawColors.accent.tableBlue;

const BASE_BEZIER_CURVATURE = 0.25;
const PAIR_CURVATURE_STEP = 0.18;

/** Legacy API/mock values (`right`, `left`) → React Flow handle ids (`source-right`, …). */
const LEGACY_SIDE_TO_SOURCE_HANDLE: Record<string, string> = {
  right: KG_HANDLE_IDS.sourceRight,
  left: KG_HANDLE_IDS.sourceLeft,
  top: KG_HANDLE_IDS.sourceTop,
  bottom: KG_HANDLE_IDS.sourceBottom,
};

const LEGACY_SIDE_TO_TARGET_HANDLE: Record<string, string> = {
  right: KG_HANDLE_IDS.targetRight,
  left: KG_HANDLE_IDS.targetLeft,
  top: KG_HANDLE_IDS.targetTop,
  bottom: KG_HANDLE_IDS.targetBottom,
};

export const normalizeDataModelSourceHandle = (handleId: string): string =>
  LEGACY_SIDE_TO_SOURCE_HANDLE[handleId] ?? handleId;

export const normalizeDataModelTargetHandle = (handleId: string): string =>
  LEGACY_SIDE_TO_TARGET_HANDLE[handleId] ?? handleId;

export const normalizeDataModelManualRelation = (
  relation: DataModelManualRelation,
): DataModelManualRelation => ({
  ...relation,
  sourceHandle: relation.sourceHandle
    ? normalizeDataModelSourceHandle(relation.sourceHandle)
    : undefined,
  targetHandle: relation.targetHandle
    ? normalizeDataModelTargetHandle(relation.targetHandle)
    : undefined,
});

const resolveRelationHandles = (
  relation: DataModelManualRelation,
): KgEdgeHandleIds => {
  if (relation.sourceHandle && relation.targetHandle) {
    return {
      sourceHandle: normalizeDataModelSourceHandle(relation.sourceHandle),
      targetHandle: normalizeDataModelTargetHandle(relation.targetHandle),
    };
  }
  return DATA_MODEL_DEFAULT_HANDLES;
};

export type BuildRelationEdgeOptions = {
  pairIndex?: number;
  selected?: boolean;
};

export const buildRelationEdge = (
  relation: DataModelManualRelation,
  options?: BuildRelationEdgeOptions,
): DataModelRelationFlowEdge => {
  const sourceNodeId = tableIdToNodeId(relation.sourceTableId);
  const targetNodeId = tableIdToNodeId(relation.targetTableId);
  const handles = resolveRelationHandles(relation);

  const pairIndex = options?.pairIndex ?? 0;
  const selected = options?.selected ?? false;
  const curvature =
    pairIndex === 0
      ? BASE_BEZIER_CURVATURE
      : (pairIndex % 2 === 1 ? -1 : 1) *
        (BASE_BEZIER_CURVATURE +
          Math.floor((pairIndex + 1) / 2) * PAIR_CURVATURE_STEP);

  return {
    id: relation.id,
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: handles.sourceHandle,
    targetHandle: handles.targetHandle,
    type: "default",
    pathOptions: { curvature },
    label: formatRelationEdgeLabel(relation),
    labelStyle: {
      fontSize: parseFloat(fontSizes["overline-4"]) * 16,
      fill: edgeLabelColor,
      fontWeight: fontWeights.medium,
      fontFamily: fontFamilies.sans,
    },
    labelBgStyle: {
      fill: rawColors.structural.white,
      fillOpacity: 0.92,
    },
    labelBgPadding: [4, 6] as [number, number],
    labelBgBorderRadius: 4,
    selected,
    interactionWidth: 24,
    style: {
      stroke: edgeLabelColor,
      strokeWidth: selected ? 2.5 : 1.5,
    },
  };
};

export const buildRelationEdges = (
  relations: readonly DataModelManualRelation[],
  selectedEdgeId?: string | null,
): DataModelRelationFlowEdge[] => {
  const pairCounts = new Map<string, number>();

  return relations.map((relation) => {
    const pairKey = `${relation.sourceTableId}|${relation.targetTableId}`;
    const pairIndex = pairCounts.get(pairKey) ?? 0;
    pairCounts.set(pairKey, pairIndex + 1);

    return buildRelationEdge(relation, {
      pairIndex,
      selected: relation.id === selectedEdgeId,
    });
  });
};
